import {
    DocumentVersion,
    PublicationForm,
    PublicationStatusEnum,
    PublicationType,
    StisPublicationAction,
} from '@web/vin-sessions/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty, isEqual } from 'lodash';
import { docsService } from '@web/services/singletons';
import { basePublicationInfo } from '../context/publications.context';
import usePublicationData from './use-publication-data';
import { AutosaveResult, useAutosave } from './use-autosave';
import { parseVersion } from '../version.utils';
import { getSaveErrorMessage } from '../save-error.utils';
import {
    PublicationSnapshot,
    SAVE_FAILED_REASON,
    getAutosaveBlockedReason,
    shouldSaveImmediately,
    toPublicationSnapshot,
} from '../autosave.utils';

export { prepareCreatePublicationBody } from '../autosave.utils';

interface Props {
    publicationFormId: string | undefined;
    publicationAction?: StisPublicationAction;
    maxVersion?: number;
    publicationTypes?: PublicationType[];
    canPublish?: boolean;
    onSaved?: (publicationInfo: PublicationForm) => void;
    autosaveDelayMs?: number;
    setCreatorUser: (
        value: React.SetStateAction<{
            firstName: string;
            lastName: string;
            name: string;
        } | null>
    ) => void;
    setUpdatedUser: (
        value: React.SetStateAction<{
            firstName: string;
            lastName: string;
            name: string;
        } | null>
    ) => void;
    setParentPublications: (value: React.SetStateAction<DocumentVersion[]>) => void;
}

// The id travels with the value so a late save never lands on another document
interface AutosaveValue {
    publicationId: string;
    publicationInfo: PublicationForm;
    forceDocument?: boolean;
}

const usePublicationFormDetails = ({
    publicationFormId,
    publicationAction,
    maxVersion,
    publicationTypes,
    canPublish = true,
    onSaved,
    autosaveDelayMs,
    setCreatorUser,
    setUpdatedUser,
    setParentPublications,
}: Props) => {
    const [publicationInfo, setPublicationInformation] = useState<PublicationForm>(basePublicationInfo);
    const [savedStatus, setSavedStatus] = useState<string>();

    const { publicationData, actions } = usePublicationData({
        publicationFormId: publicationFormId,
        publicationAction: publicationAction,
        setCreatorUser,
        setUpdatedUser,
        setParentPublications,
    });

    const savedSnapshotRef = useRef<PublicationSnapshot | null>(
        publicationAction === StisPublicationAction.New ? toPublicationSnapshot(basePublicationInfo) : null
    );
    const scheduledSnapshotRef = useRef<PublicationSnapshot | null>(savedSnapshotRef.current);
    const currentIdRef = useRef(publicationFormId);
    currentIdRef.current = publicationFormId;
    const onSavedRef = useRef(onSaved);
    onSavedRef.current = onSaved;
    // Read by saveNow, which runs from a polling timer long after the render that made it
    const publicationInfoRef = useRef<PublicationForm>(basePublicationInfo);
    // The document the form state belongs to. Until the one being opened is loaded, the form still
    // holds the last one, and saving that would write it over the document now in the URL.
    const loadedIdRef = useRef(publicationFormId);

    const setPublicationInfo = useCallback((nextState: Partial<PublicationForm>) => {
        setPublicationInformation((prevState) => ({ ...prevState, ...nextState }));
    }, []);

    useEffect(() => {
        if (isEmpty(publicationData) || !publicationData) {
            return;
        }
        if (publicationData.documentId && publicationData.documentId !== publicationFormId) {
            return;
        }
        const loaded = {
            documentId: publicationData.documentId,
            title: publicationData.title === null ? 'Untitled' : publicationData.title,
            docCode: publicationData.docCode,
            visibilityLevel: publicationData.visibilityLevel,
            url: publicationData.url,
            contentFile: publicationData.contentFile,
            createdDate: publicationData.createdDate,
            keywords: publicationData.keywords ? publicationData.keywords : [],
            dtcCodes: publicationData.dtcCodes ? publicationData.dtcCodes : [],
            toolNumbers: publicationData.toolNumbers,
            description: publicationData.description,
            status: publicationData.status,
            docTypeId: publicationData.docTypeId
                ? publicationData.docTypeId
                : publicationTypes?.find((type) => type.code === publicationData.docType)?.id,
            publicationType:
                publicationData.docTypeId === null
                    ? { label: '', code: '', id: '', description: '' }
                    : publicationData.docTypeId
                      ? publicationTypes?.find((type) => type.id === publicationData.docTypeId)
                      : publicationData.publicationType,
            createdAccount: publicationData.createdAccount,
            lastUpdatedAccount: publicationData.lastUpdatedAccount,
            createdBy: publicationData.createdBy,
            updatedBy: publicationData.updatedBy,
            lastUpdated: publicationData.lastUpdated,
            createdTimestamp: publicationData.createdDate
                ? new Date(publicationData.createdDate).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: 'numeric',
                      hour12: true,
                  })
                : undefined,
            createdTimestampForDisplay: publicationData.createdTimestampForDisplay,
            lastUpdatedTimestamp: publicationData.lastUpdatedTimestamp,
            lastUpdatedTimestampForDisplay: publicationData.lastUpdatedTimestampForDisplay,
            parentDocumentId: publicationData.parentDocumentId,
            uploadStatus: publicationData.uploadStatus,
            documentsVersions: publicationData.documentsVersions || [],
            pubVersion:
                parseVersion(publicationData.pubVersion) ??
                (publicationAction === StisPublicationAction.AddVersion ? maxVersion : undefined) ??
                1,
            associatedDocuments: publicationData.associatedDocuments,
            documentsVehicles: publicationData.documentsVehicles,
            publishedYear: publicationData.publishedYear || undefined,
            publishedMonth: publicationData.publishedMonth || undefined,
            interactiveInd: publicationData.interactiveInd ?? undefined,
        } as PublicationForm;

        // The loaded document is the saved state, so loading it never triggers a save
        savedSnapshotRef.current = toPublicationSnapshot(loaded);
        scheduledSnapshotRef.current = savedSnapshotRef.current;
        loadedIdRef.current = publicationFormId;
        setSavedStatus(savedSnapshotRef.current.document.status);
        setPublicationInfo(loaded);
    }, [maxVersion, setPublicationInfo, publicationAction, publicationData, publicationFormId]);

    // The publication types can arrive after the document. Fill in the type then, without
    // treating it as a change, instead of reloading the form over whatever the user typed.
    useEffect(() => {
        if (publicationInfo.publicationType || !publicationInfo.docTypeId || !publicationTypes?.length) {
            return;
        }
        const publicationType = publicationTypes.find((type) => type.id === publicationInfo.docTypeId);
        if (!publicationType) {
            return;
        }
        const withType = (snapshot: PublicationSnapshot | null) =>
            snapshot && { ...snapshot, document: { ...snapshot.document, docTypeId: publicationType.id } };
        savedSnapshotRef.current = withType(savedSnapshotRef.current);
        scheduledSnapshotRef.current = withType(scheduledSnapshotRef.current);
        setPublicationInfo({ publicationType });
    }, [publicationTypes, publicationInfo.publicationType, publicationInfo.docTypeId, setPublicationInfo]);

    const persist = useCallback(
        async ({ publicationId, publicationInfo: info, forceDocument }: AutosaveValue): Promise<AutosaveResult> => {
            const next = toPublicationSnapshot(info);
            const isCurrentDocument = () => currentIdRef.current === publicationId;
            const saved = isCurrentDocument() ? savedSnapshotRef.current : null;

            if (forceDocument || !saved || !isEqual(next.document, saved.document)) {
                const response = await docsService.editPublication({ publicationId, body: next.document });
                if (!response.success) {
                    return {
                        ok: false,
                        message: response.aborted
                            ? undefined
                            : getSaveErrorMessage(response.data, 'The publication was not saved'),
                    };
                }
                if (isCurrentDocument()) {
                    savedSnapshotRef.current = { ...(savedSnapshotRef.current ?? next), document: next.document };
                    // A body without a status leaves the backend's as it was
                    if (next.document.status) {
                        setSavedStatus(next.document.status);
                    }
                }
            }

            if (!saved || !isEqual(next.tags, saved.tags)) {
                const response = await docsService.saveTags({ documentId: publicationId, body: next.tags });
                if (!response.success) {
                    return {
                        ok: false,
                        message: response.aborted
                            ? undefined
                            : getSaveErrorMessage(response.data, 'The keywords and codes were not saved'),
                    };
                }
                if (isCurrentDocument()) {
                    savedSnapshotRef.current = { ...(savedSnapshotRef.current ?? next), tags: next.tags };
                }
            }

            if (isCurrentDocument()) {
                onSavedRef.current?.(info);
            }
            return { ok: true };
        },
        []
    );

    const autosave = useAutosave<AutosaveValue>({ save: persist, delayMs: autosaveDelayMs });

    const blockedReason = useMemo(
        () => getAutosaveBlockedReason(publicationInfo, { canPublish, savedStatus }),
        [publicationInfo, canPublish, savedStatus]
    );
    const blockedReasonRef = useRef(blockedReason);
    blockedReasonRef.current = blockedReason;

    // Read when the user leaves, so it sees the save that failed last
    const lastSaveErrorRef = useRef<string>();
    lastSaveErrorRef.current = autosave.status === 'error' ? (autosave.errorMessage ?? SAVE_FAILED_REASON) : undefined;

    const getUnsavedChangesReason = useCallback(() => {
        if (lastSaveErrorRef.current) {
            return lastSaveErrorRef.current;
        }
        const saved = savedSnapshotRef.current;
        if (!saved || !blockedReasonRef.current) {
            return null;
        }
        return isEqual(toPublicationSnapshot(publicationInfoRef.current), saved) ? null : blockedReasonRef.current;
    }, []);

    publicationInfoRef.current = publicationInfo;

    const { schedule, flush } = autosave;

    /**
     * Applies the changes and sends the whole document, even when nothing else changed. Used once an
     * upload completes, so the publish rule is not applied; the other save rules still are.
     *
     * @param changes fields to set before saving
     */
    const saveNow = useCallback(
        (changes: Partial<PublicationForm> = {}) => {
            const info = { ...publicationInfoRef.current, ...changes };
            publicationInfoRef.current = info;
            setPublicationInfo(changes);
            if (!publicationFormId || loadedIdRef.current !== publicationFormId || getAutosaveBlockedReason(info)) {
                return;
            }
            scheduledSnapshotRef.current = toPublicationSnapshot(info);
            schedule(
                { publicationId: publicationFormId, publicationInfo: info, forceDocument: true },
                { immediate: true }
            );
        },
        [publicationFormId, setPublicationInfo, schedule]
    );

    const saveAsDraft = useCallback(async (): Promise<AutosaveResult> => {
        if (!publicationFormId) {
            return { ok: false };
        }
        // Let a save already on its way land first, so this one is the last word
        await flush();
        const info = { ...publicationInfoRef.current, status: PublicationStatusEnum.Draft };
        const reason = getAutosaveBlockedReason(info, { savedStatus });
        if (reason) {
            return { ok: false, message: reason };
        }
        let result: AutosaveResult;
        try {
            result = await persist({ publicationId: publicationFormId, publicationInfo: info, forceDocument: true });
        } catch {
            result = { ok: false };
        }
        if (result.ok) {
            publicationInfoRef.current = info;
            scheduledSnapshotRef.current = toPublicationSnapshot(info);
            setPublicationInfo({ status: PublicationStatusEnum.Draft });
        }
        return result;
    }, [publicationFormId, savedStatus, flush, persist, setPublicationInfo]);

    useEffect(() => {
        const previous = scheduledSnapshotRef.current;
        if (!publicationFormId || !previous || blockedReason || loadedIdRef.current !== publicationFormId) {
            return;
        }
        const next = toPublicationSnapshot(publicationInfo);
        if (isEqual(next, previous)) {
            return;
        }
        scheduledSnapshotRef.current = next;
        schedule(
            { publicationId: publicationFormId, publicationInfo },
            { immediate: shouldSaveImmediately(previous, next) }
        );
    }, [publicationInfo, blockedReason, publicationFormId, schedule]);

    useEffect(() => {
        if (publicationFormId) {
            actions.fetchData();
        }
    }, [publicationFormId]);

    return {
        publicationInfo,
        setPublicationInfo,
        saveNow,
        saveAsDraft,
        getUnsavedChangesReason,
        savedStatus,
        autosave: {
            status: autosave.status,
            savedAt: autosave.savedAt,
            errorMessage: autosave.errorMessage,
            blockedReason,
            retry: autosave.retry,
            flush: autosave.flush,
            markSaved: autosave.markSaved,
            hasUnsavedChanges: autosave.hasUnsavedChanges,
        },
    };
};

export default usePublicationFormDetails;
